import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { useTranslation } from "react-i18next";
import { fetchTeamBoard } from "../../../api/client";

export function pickDoneColumn(columns = []) {
  return columns.find((c) => c.isDone) || null;
}

export function pickReopenColumn(columns = [], currentColumnId) {
  const open = columns.filter((c) => !c.isDone);
  if (!open.length) return null;
  const current = open.find((c) => c.id === currentColumnId);
  return current || open[0];
}

function TaskColumnActions({
  boardId,
  cardId,
  columnId,
  columnIsDone = false,
  columns: columnsProp,
  onMove,
  onComplete,
  onReopen,
  compact = false,
  disabled = false,
  stopPropagation = true,
}) {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [columns, setColumns] = useState(columnsProp || null);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (columnsProp?.length) setColumns(columnsProp);
  }, [columnsProp]);

  const ensureColumns = useCallback(async () => {
    if (columns?.length) return columns;
    if (!boardId) return [];
    setLoading(true);
    try {
      const board = await fetchTeamBoard(boardId);
      const cols = board?.columns || [];
      setColumns(cols);
      return cols;
    } finally {
      setLoading(false);
    }
  }, [boardId, columns]);

  const handleWrapClick = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  const handleSelectOpen = async () => {
    await ensureColumns();
  };

  const handleColumnChange = async (e) => {
    const nextColumnId = Number(e.target.value);
    if (!nextColumnId || nextColumnId === columnId || moving) return;
    setMoving(true);
    try {
      await onMove?.(nextColumnId);
    } finally {
      setMoving(false);
    }
  };

  const handleComplete = async () => {
    if (moving || columnIsDone) return;
    setMoving(true);
    try {
      const cols = await ensureColumns();
      const doneCol = pickDoneColumn(cols);
      if (doneCol) await onMove?.(doneCol.id);
      else await onComplete?.();
    } finally {
      setMoving(false);
    }
  };

  const handleReopen = async () => {
    if (moving || !columnIsDone) return;
    setMoving(true);
    try {
      const cols = await ensureColumns();
      const reopenCol = pickReopenColumn(cols, columnId);
      if (reopenCol) await onMove?.(reopenCol.id);
      else await onReopen?.();
    } finally {
      setMoving(false);
    }
  };

  const selectHeight = compact ? 28 : 32;

  return (
    <Box
      onClick={handleWrapClick}
      onMouseDown={stopPropagation ? (e) => e.stopPropagation() : undefined}
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
    >
      {!columnIsDone ? (
        <Tooltip title={t("completeTask")}>
          <span>
            <IconButton
              size="small"
              disabled={disabled || moving}
              onClick={handleComplete}
              aria-label={t("completeTask")}
              sx={{
                width: compact ? 28 : 32,
                height: compact ? 28 : 32,
                color: "success.main",
                bgcolor: alpha(theme.palette.success.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.16) },
              }}
            >
              {moving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: compact ? 18 : 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={t("reopenTask")}>
          <span>
            <IconButton
              size="small"
              disabled={disabled || moving}
              onClick={handleReopen}
              aria-label={t("reopenTask")}
              sx={{
                width: compact ? 28 : 32,
                height: compact ? 28 : 32,
                color: "success.main",
                bgcolor: alpha(theme.palette.success.main, 0.12),
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.2) },
              }}
            >
              {moving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ReplayOutlinedIcon sx={{ fontSize: compact ? 18 : 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Select
        size="small"
        value={columnId || ""}
        onChange={handleColumnChange}
        onOpen={handleSelectOpen}
        disabled={disabled || moving || !columnId}
        displayEmpty
        renderValue={(value) => {
          const col = columns?.find((c) => c.id === value);
          return col?.name || t("moveTaskColumn");
        }}
        sx={{
          minWidth: compact ? 108 : 128,
          maxWidth: compact ? 148 : 180,
          height: selectHeight,
          fontSize: compact ? "0.75rem" : "0.8125rem",
          fontWeight: 600,
          borderRadius: 1.5,
          bgcolor: columnIsDone
            ? alpha(theme.palette.success.main, 0.1)
            : alpha(theme.palette.background.paper, 0.9),
          color: columnIsDone ? "success.dark" : "inherit",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: columnIsDone
              ? alpha(theme.palette.success.main, 0.45)
              : undefined,
          },
          "& .MuiSelect-select": { py: 0.5, pr: "28px !important" },
        }}
      >
        {loading && !columns?.length ? (
          <MenuItem disabled value="">
            <CircularProgress size={14} sx={{ mr: 1 }} />
            {t("loadingColumns")}
          </MenuItem>
        ) : null}
        {(columns || []).map((col) => (
          <MenuItem key={col.id} value={col.id} sx={{ fontSize: "0.8125rem" }}>
            {col.name}
            {col.isDone ? ` (${t("doneColumnShort")})` : ""}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

export default TaskColumnActions;
